import { z } from 'zod';
import { TenantIdSchema, IsoDateSchema } from './_primitives.js';

// ── Module catalog constants ──────────────────────────────────────────────────
// Canonical 39-ID catalog from MODULES-1 PR-1. Both constant map and Zod enum
// are exported per project conventions (CLAUDE.md TYPES-1 rule).

export const CATALOG_MODULE_IDS = Object.freeze({
  TMS:            'TMS',
  TMS_PARCEL:     'TMS-PARCEL',
  TMS_LTL:        'TMS-LTL',
  TMS_FTL:        'TMS-FTL',
  TMS_OCEAN:      'TMS-OCEAN',
  TMS_AIR:        'TMS-AIR',
  TMS_INTERMODAL: 'TMS-INTERMODAL',
  TMS_DRAYAGE:    'TMS-DRAYAGE',
  WMS:            'WMS',
  WMS_SP:         'WMS-SP',
  IMS:            'IMS',
  IMS_SP:         'IMS-SP',
  IMS_D:          'IMS-D',
  CX:             'CX',
  COMMS:          'COMMS',
  CRM:            'CRM',
  INTEL:          'INTEL',
  INTL:           'INTL',
  DOC:            'DOC',
  IOT:            'IOT',
  FIN:            'FIN',
  FLEET:          'FLEET',
  AEGIS:          'AEGIS',
  AEGIS_GOV:      'AEGIS-GOV',
  AHI:            'AHI',
  ARTEMIS:        'ARTEMIS',
  DRIVER:         'DRIVER',
  IT:             'IT',
  SUSTAINABILITY: 'SUSTAINABILITY',
  NETWORK:        'NETWORK',
  TRUST:          'TRUST',
  PORTAL:         'PORTAL',
  OPS:            'OPS',
  SYS:            'SYS',
  ORCH:           'ORCH',
  GRAPH:          'GRAPH',
  OVERVIEW:       'OVERVIEW',
  EXEC:           'EXEC',
  COUNCIL:        'COUNCIL',
});

// Platform infrastructure — always on, never user-toggleable.
export const PLATFORM_INFRA_MODULE_IDS = Object.freeze([
  'DOC', 'AEGIS-GOV', 'SYS', 'ORCH', 'GRAPH',
]);

export const ModuleIdEnum = z.enum(Object.values(CATALOG_MODULE_IDS));

// ── Audit log entry ───────────────────────────────────────────────────────────

export const AuditLogSchema = z.object({
  tenantId:   TenantIdSchema,
  userId:     z.string().min(1),
  action:     z.string().min(1),
  entityType: z.string().min(1),
  entityId:   z.string().nullable().optional(),
  changes:    z.record(z.string(), z.any()).nullable().optional(),
  userAgent:  z.string().optional(),
  timestamp:  z.union([IsoDateSchema, z.object({})]),
}).passthrough();

// ── Validation telemetry (OBS-1 consumer) ─────────────────────────────────────

export const ValidationTelemetrySchema = z.object({
  tenantId:     TenantIdSchema,
  callerModule: z.string().min(1),
  schemaName:   z.string().min(1),
  reasonCode:   z.string().min(1),
  timestamp:    IsoDateSchema,
});

// ── Tenant record ─────────────────────────────────────────────────────────────

export const DEPLOYMENT_MODE = Object.freeze({
  FIVE_PL:           '5pl',
  IN_TENANT:         'in_tenant',
  FOUR_PL:           '4pl',
  THREE_PL:          '3pl',
  MANAGED_TRANSPORT: 'managed_transport',
});

export const DeploymentModeEnum = z.enum(Object.values(DEPLOYMENT_MODE));

export const TenantSchema = z.object({
  tenantId:            TenantIdSchema,
  name:                z.string().min(1),
  status:              z.string().optional(),
  plan:                z.string().optional(),
  deploymentMode:      DeploymentModeEnum.optional(),
  primaryContactEmail: z.string().email().optional(),
  softDeleted:         z.boolean().optional(),
  createdAt:           z.union([IsoDateSchema, z.object({})]).optional(),
  updatedAt:           z.union([IsoDateSchema, z.object({})]).optional(),
  // MODULES-1: per-tenant module gate fields
  enabled_modules:     z.array(ModuleIdEnum).optional(),
  module_settings:     z.record(z.string(), z.any()).optional(),
  module_enabled_at:   z.record(z.string(), z.any()).optional(),
}).passthrough();

/** @typedef {import('zod').infer<typeof AuditLogSchema>} AuditLog */
/** @typedef {import('zod').infer<typeof ValidationTelemetrySchema>} ValidationTelemetry */
/** @typedef {import('zod').infer<typeof TenantSchema>} Tenant */
